import { Bar, BarConfig } from '@ant-design/plots';
import { useLazyQuery } from '@apollo/client';
import { GET_INSTITUTIONS_BY_TOPICS } from '@frontend/data-access';
import { Card, Select } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

function PopularTopicsStats({ className }: { className?: string }) {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<{ topic: string; count: number }[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);

  const [getData, { loading }] = useLazyQuery<{
    companyCountByTopic: {
      count: number;
      topic: string;
    }[];
  }>(GET_INSTITUTIONS_BY_TOPICS, {
    onCompleted: (data) => {
      setData(data.companyCountByTopic);
    },
  });

  useEffect(() => {
    getData({ variables: { order: true } });
  }, [getData]);

  const filteredData = useMemo(() => {
    if (selectedTopics.length === 0) return data;
    return data.filter((item) => selectedTopics.includes(item.topic));
  }, [data, selectedTopics]);

  const config: BarConfig = {
    data: filteredData,
    xField: 'count',
    yField: 'topic',
    color: '#3BA1FF',
    appendPadding: [0, 20, 0, 0],
    yAxis: {
      label: {
        formatter: (value) =>
          value.length > 30 ? value.slice(0, 30) + '...' : value,
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
      },
    },
    scale: {
      color: {
        range: ['#58AFFF'],
      },
    },
    axis: {
      x: {
        label: {
          style: {
            fontFamily: 'Lufga',
            fill: 'rgba(0, 0, 0, 0.65)',
            textAlign: 'right',
          },
        },
        labelFormatter: (value) =>
          value.length > 30 ? value.slice(0, 30) + '...' : value,
      },
    },
    label: {
      text: 'count',
      position: 'right',
      textAlign: 'start',
      dx: 8,
      style: {
        fill: 'rgba(0, 0, 0, 0.65)',
        fontFamily: 'Lufga',
      },
    },
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
      className={`${className}`}
    >
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-900 font-medium">
            {t('companies_by_topics')}
          </div>
          <div>
            <Select
              mode="multiple"
              optionFilterProp="label"
              style={{ width: 300 }}
              placeholder={t('select_topics')}
              value={selectedTopics}
              onChange={setSelectedTopics}
              options={data.map((item) => ({
                value: item.topic,
                label: item.topic,
              }))}
              maxTagCount="responsive"
              allowClear
            />
          </div>
        </div>
        <Bar loading={loading} {...config} />
      </div>
    </Card>
  );
}

export default PopularTopicsStats;
